/**
 * DOM指标空状态监控系统 - 简化监控执行器
 */

import type { 
  ISimpleMonitorConfig, 
  IElementConfig,
  IEnhancedElementConfig,
  IDetectionResult
} from '../types/simple';
import { SimpleDOMScanner } from './SimpleDOMScanner';
import { SimpleEmptyStateDetector } from './SimpleEmptyStateDetector';
import { SIMPLE_DEFAULT_SCANNER_OPTIONS } from '../types/simple';

/**
 * 简化的监控执行器
 * 负责协调DOM扫描和空状态检测
 */
export class SimpleMetricsExecutor {
  private scanner: SimpleDOMScanner;
  private detector: SimpleEmptyStateDetector;
  private config: ISimpleMonitorConfig;
  private detectionTimer: NodeJS.Timeout | null = null;
  private isRunning = false;

  // 回调函数
  onDetection?: (emptyElements: IEnhancedElementConfig[], statistics: {
    totalElements: number;
    emptyElements: number;
    emptyRatio: number;
  }) => void;

  constructor(config: ISimpleMonitorConfig) {
    this.config = config;
    this.scanner = new SimpleDOMScanner(SIMPLE_DEFAULT_SCANNER_OPTIONS);
    this.detector = new SimpleEmptyStateDetector();
  }

  /**
   * 开始监控
   */
  start(): void {
    if (!this.config.enabled) {
      // console.log('[SimpleMetricsExecutor] 监控已禁用');
      return;
    }

    if (this.isRunning) {
      // console.log('[SimpleMetricsExecutor] 监控已在运行中');
      return;
    }

    this.isRunning = true;
    this.scheduleDetection();
    // console.log('[SimpleMetricsExecutor] 监控已启动');
  }

  /**
   * 停止监控
   */
  stop(): void {
    this.isRunning = false;
    
    if (this.detectionTimer) {
      clearTimeout(this.detectionTimer);
      this.detectionTimer = null;
      // console.log('[SimpleMetricsExecutor] 监控已停止');
    }
  }

  /**
   * 立即执行一次检测
   */
  detectNow(): void {
    if (!this.config.enabled) {
      return;
    }
    
    this.executeDetection();
  }

  /**
   * 调度延迟检测
   */
  private scheduleDetection(): void {
    if (!this.isRunning) {
      return;
    }

    this.detectionTimer = setTimeout(() => {
      this.executeDetection();
    }, this.config.detectionDelay);
  }

  /**
   * 执行检测
   */
  private executeDetection(): void {
    try {
      // console.log('[SimpleMetricsExecutor] 开始执行检测');
      
      // 1. 扫描标记的元素
      const scanResult = this.scanner.scanMetricsElements();
      
      if (scanResult.elements.length === 0) {
        console.log('[SimpleMetricsExecutor] 未发现标记的元素');
        return;
      }
      
      // console.log(`[SimpleMetricsExecutor] 发现 ${scanResult.elements.length} 个标记元素`);
      
      // 2. 检测所有元素的状态
      const emptyElements: IEnhancedElementConfig[] = [];
      const allElements = scanResult.elements;
      
        for (const elementConfig of allElements) {
          const detectionResult = this.detector.isElementEmpty(elementConfig.element);
          
          if (detectionResult.isEmpty) {
            // 收集空元素的基本信息
            const element = elementConfig.element;
            const enhancedConfig: IEnhancedElementConfig = {
              ...elementConfig,
              emptyReason: detectionResult.reason || '未知原因',
              detectedAt: Date.now(),
              tagName: element.tagName.toLowerCase(),
              className: element.className || undefined,
              textContent: (element.textContent || '').trim().substring(0, 100) || undefined,
              childrenCount: element.children.length
            };
            
            emptyElements.push(enhancedConfig);
            // console.log(`[SimpleMetricsExecutor] 发现空指标: ${elementConfig.name} - ${detectionResult.reason}`);
          }
        }
      
      // 3. 构建检测统计信息
      const statistics = {
        totalElements: allElements.length,
        emptyElements: emptyElements.length,
        emptyRatio: allElements.length > 0 ? emptyElements.length / allElements.length : 0
      };
      
      const emptyPercentage = `${(statistics.emptyRatio * 100).toFixed(1)}%`;
      console.log(`[SimpleMetricsExecutor] 检测统计: 总元素${statistics.totalElements}个, 空元素${statistics.emptyElements}个, 比例${emptyPercentage}`);
      
      // 4. 触发回调（传递统计信息）
      if (emptyElements.length > 0 && this.onDetection) {
        // console.log(`[SimpleMetricsExecutor] 上报 ${emptyElements.length} 个空指标`);
        this.onDetection(emptyElements, statistics);
      }
      
    } catch (error) {
      console.error('[SimpleMetricsExecutor] 检测执行失败:', error);
    }
  }

  /**
   * 更新配置
   */
  updateConfig(newConfig: Partial<ISimpleMonitorConfig>): void {
    this.config = { ...this.config, ...newConfig };
    
    // 如果监控被禁用，停止当前执行
    if (!this.config.enabled && this.isRunning) {
      this.stop();
    }
    
    // console.log('[SimpleMetricsExecutor] 配置已更新', this.config);
  }

  /**
   * 获取当前状态
   */
  getStatus(): {
    isRunning: boolean;
    config: ISimpleMonitorConfig;
    scannerStats: { scanCount: number };
  } {
    return {
      isRunning: this.isRunning,
      config: this.config,
      scannerStats: this.scanner.getStats()
    };
  }

  /**
   * 销毁执行器
   */
  destroy(): void {
    this.stop();
    this.onDetection = undefined;
    console.log('[SimpleMetricsExecutor] 执行器已销毁');
  }
} 